import React, { useState, useEffect, useCallback } from 'react';
import {
  getContainersView, assignOrdersToContainer, searchCars,
  updateContainer, deleteContainer, removeCarFromContainer,
} from '../../services/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Plus, Pencil, Trash, MagnifyingGlass, X, ArrowsClockwise, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  new_order: 'New', order_submitted: 'Submitted', ready_to_load: 'Ready',
  loaded: 'Loaded', shipped: 'Shipped', arrived_alg: 'Arrived ALG',
  cleared_port: 'Cleared', delivered: 'Delivered', active: 'Active',
};

const STATUS_COLORS = {
  shipped: 'bg-violet-600 text-white', arrived_alg: 'bg-teal-100 text-teal-800',
  delivered: 'bg-green-600 text-white', loaded: 'bg-purple-100 text-purple-800',
  cleared_port: 'bg-cyan-100 text-cyan-800',
};

const emptyCreateForm = {
  container_number: '', shipping_company: '', vessel_number: '', bl_number: '', eta: '',
  car_ids: [],
};

const ContainersPage = () => {
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // Create / Assign
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyCreateForm);
  const [saving, setSaving] = useState(false);
  // Car picker
  const [pickerQuery, setPickerQuery] = useState('');
  const [unassignedOnly, setUnassignedOnly] = useState(true);
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  // Force-on-conflict
  const [conflictDlg, setConflictDlg] = useState(null); // { conflicts, payload }

  // Edit container
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ new_container_number: '', shipping_company: '', vessel_number: '', bl_number: '', eta: '' });
  // Edit-dialog car picker (add cars)
  const [editPickerQuery, setEditPickerQuery] = useState('');
  const [editUnassignedOnly, setEditUnassignedOnly] = useState(true);
  const [editPickerResults, setEditPickerResults] = useState([]);
  const [editPickerLoading, setEditPickerLoading] = useState(false);
  const [editCarIdsToAdd, setEditCarIdsToAdd] = useState([]);
  const [forceAddCtx, setForceAddCtx] = useState(null); // used when conflict dialog opens from edit flow

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await getContainersView(params);
      setContainers(res.data || []);
    } catch {
      toast.error('Failed to load containers');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { loadData(); }, [loadData]);

  // ===== Car picker =====
  const runSearch = useCallback(async () => {
    setPickerLoading(true);
    try {
      const res = await searchCars({ q: pickerQuery, unassigned_only: unassignedOnly, limit: 50 });
      setPickerResults(res.data?.cars || []);
    } catch {
      toast.error('Search failed');
    } finally {
      setPickerLoading(false);
    }
  }, [pickerQuery, unassignedOnly]);

  // Auto-search on dialog open + when toggle flips
  useEffect(() => {
    if (createOpen) runSearch();
  }, [createOpen, unassignedOnly, runSearch]);

  const openCreate = () => {
    setForm(emptyCreateForm);
    setPickerQuery('');
    setPickerResults([]);
    setUnassignedOnly(true);
    setCreateOpen(true);
  };

  const toggleCar = (carId) => {
    setForm(prev => {
      const ids = new Set(prev.car_ids);
      if (ids.has(carId)) ids.delete(carId); else ids.add(carId);
      return { ...prev, car_ids: Array.from(ids) };
    });
  };

  const doAssign = async (force = false) => {
    const payload = {
      container_number: form.container_number.trim().toUpperCase(),
      order_ids: [],
      car_ids: form.car_ids,
      force,
      shipping_company: form.shipping_company || undefined,
      vessel_number: form.vessel_number || undefined,
      bl_number: form.bl_number || undefined,
      eta: form.eta || undefined,
    };
    const res = await assignOrdersToContainer(payload);
    return res;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.container_number.trim() || form.car_ids.length === 0) {
      toast.error('Container number and at least one car are required');
      return;
    }
    setSaving(true);
    try {
      const res = await doAssign(false);
      toast.success(`Container ${res.data.container_number} created · ${res.data.cars_updated} cars assigned`);
      setCreateOpen(false);
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 409 conflict — backend returned structured payload
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.conflicts) {
        setConflictDlg({ conflicts: detail.conflicts, message: detail.message });
      } else {
        toast.error((typeof detail === 'string' && detail) || 'Failed to assign');
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmForceMove = async () => {
    setSaving(true);
    try {
      // Conflict dialog can come from EITHER the create flow OR the edit flow.
      if (forceAddCtx) {
        const addRes = await assignOrdersToContainer({
          container_number: forceAddCtx.container_number,
          car_ids: forceAddCtx.car_ids,
          force: true,
        });
        toast.success(`${addRes.data.cars_updated} car(s) moved into ${forceAddCtx.container_number}`);
        setForceAddCtx(null);
        setConflictDlg(null);
        setEditOpen(false);
        loadData();
      } else {
        const res = await doAssign(true);
        toast.success(`${res.data.cars_updated} cars moved to ${res.data.container_number}`);
        setConflictDlg(null);
        setCreateOpen(false);
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move');
    } finally {
      setSaving(false);
    }
  };

  // ===== Edit container =====
  const openEdit = (cont) => {
    setEditTarget(cont);
    setEditForm({
      new_container_number: cont.container_number,
      shipping_company: cont.shipping_company || '',
      vessel_number: cont.vessel || '',
      bl_number: '',
      eta: cont.eta || '',
    });
    setEditPickerQuery('');
    setEditPickerResults([]);
    setEditCarIdsToAdd([]);
    setEditUnassignedOnly(true);
    setEditOpen(true);
  };

  const runEditSearch = useCallback(async () => {
    setEditPickerLoading(true);
    try {
      const res = await searchCars({ q: editPickerQuery, unassigned_only: editUnassignedOnly, limit: 50 });
      // Exclude cars already in THIS container (no point re-adding them)
      const list = (res.data?.cars || []).filter(c => c.container_number !== editTarget?.container_number);
      setEditPickerResults(list);
    } catch {
      toast.error('Search failed');
    } finally {
      setEditPickerLoading(false);
    }
  }, [editPickerQuery, editUnassignedOnly, editTarget]);

  // Re-run search whenever Edit dialog opens or toggle flips
  useEffect(() => {
    if (editOpen) runEditSearch();
  }, [editOpen, editUnassignedOnly, runEditSearch]);

  const toggleEditCar = (carId) => {
    setEditCarIdsToAdd(prev => {
      const ids = new Set(prev);
      if (ids.has(carId)) ids.delete(carId); else ids.add(carId);
      return Array.from(ids);
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    const targetNum = editTarget.container_number;
    const newNum = editForm.new_container_number.trim().toUpperCase();
    const metaPayload = {};
    if (newNum && newNum !== targetNum) metaPayload.new_container_number = newNum;
    ['shipping_company', 'vessel_number', 'bl_number', 'eta'].forEach(k => {
      if (editForm[k]) metaPayload[k] = editForm[k];
    });
    const hasMetaChanges = Object.keys(metaPayload).length > 0;
    const hasCarsToAdd = editCarIdsToAdd.length > 0;
    if (!hasMetaChanges && !hasCarsToAdd) {
      toast.info('Nothing to change');
      return;
    }
    try {
      let finalContainerNumber = targetNum;
      // 1) Metadata / rename first (so subsequent assigns target the new name)
      if (hasMetaChanges) {
        const res = await updateContainer(targetNum, metaPayload);
        finalContainerNumber = res.data.container_number || targetNum;
        toast.success(`Container updated · ${res.data.cars_updated} cars`);
      }
      // 2) Then add new cars to the (possibly renamed) container
      if (hasCarsToAdd) {
        try {
          const addRes = await assignOrdersToContainer({
            container_number: finalContainerNumber,
            car_ids: editCarIdsToAdd,
            force: false,
          });
          toast.success(`${addRes.data.cars_updated} car(s) added to ${finalContainerNumber}`);
          setEditOpen(false);
          loadData();
        } catch (err) {
          const detail = err.response?.data?.detail;
          if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.conflicts) {
            // Re-purpose the existing conflict dialog. Stash payload for force-retry.
            // (Edit dialog remains open so the user can decide.)
            setForceAddCtx({
              container_number: finalContainerNumber,
              car_ids: editCarIdsToAdd,
            });
            setConflictDlg({ conflicts: detail.conflicts, message: detail.message, source: 'edit' });
          } else {
            toast.error((typeof detail === 'string' && detail) || 'Failed to add cars');
          }
        }
      } else {
        setEditOpen(false);
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDeleteContainer = async (cont) => {
    if (!window.confirm(`Delete container ${cont.container_number}? Its ${cont.car_count} car(s) will become unassigned (orders are kept intact).`)) return;
    try {
      const res = await deleteContainer(cont.container_number);
      toast.success(res.data.message || 'Container deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRemoveCar = async (containerNumber, carId, label) => {
    if (!window.confirm(`Remove ${label || 'this car'} from ${containerNumber}? It will become unassigned.`)) return;
    try {
      await removeCarFromContainer(containerNumber, carId);
      toast.success('Car removed from container');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove car');
    }
  };

  const totalCars = containers.reduce((s, c) => s + (c.car_count || 0), 0);

  return (
    <div data-testid="containers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Container View</h1>
          <p className="text-muted-foreground text-sm">{containers.length} containers · {totalCars} cars</p>
        </div>
        <Button onClick={openCreate} className="btn-primary" data-testid="create-container-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Container
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white border border-border p-3 mb-4">
        <Input placeholder="Search container # or BL #..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9" data-testid="container-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : containers.length === 0 ? (
        <div className="text-center py-16 bg-white border border-border text-muted-foreground">
          <p className="text-base font-medium">No containers found</p>
          <p className="text-sm mt-1">Cars with container numbers will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {containers.map(cont => (
            <div key={cont.container_number} className="bg-white border-2 border-border overflow-hidden" data-testid={`container-${cont.container_number}`}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === cont.container_number ? null : cont.container_number)}
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-base font-bold">{cont.container_number}</span>
                      <Badge variant="secondary" className="text-[10px]">{cont.car_count} car{cont.car_count !== 1 ? 's' : ''}</Badge>
                      {cont.statuses?.map(s => (
                        <Badge key={s} className={`text-[10px] ${STATUS_COLORS[s] || 'bg-gray-100 text-gray-700'}`}>
                          {STATUS_LABELS[s] || s}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      {cont.shipping_company && <span>{cont.shipping_company}</span>}
                      {cont.vessel && <span>· Vessel: {cont.vessel}</span>}
                      {cont.eta && <span>· ETA: {cont.eta}</span>}
                    </div>
                    {cont.order_numbers?.length > 0 && (
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {cont.order_numbers.map(num => (
                          <span key={num} className="text-[10px] font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 border border-blue-200">{num}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(cont)} data-testid={`container-edit-${cont.container_number}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" title="Delete container" onClick={() => handleDeleteContainer(cont)} data-testid={`container-delete-${cont.container_number}`}>
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {/* Tracking */}
                {cont.tracking?.tracking_data && (
                  <div className="mt-2 pt-2 border-t flex flex-wrap gap-3 text-xs">
                    {cont.tracking.tracking_data.port_of_lading && <span><span className="text-muted-foreground">From:</span> {cont.tracking.tracking_data.port_of_lading}</span>}
                    {cont.tracking.tracking_data.port_of_discharge && <span><span className="text-muted-foreground">To:</span> {cont.tracking.tracking_data.port_of_discharge}</span>}
                    {cont.tracking.tracking_data.pod_eta && <span><span className="text-muted-foreground">ETA:</span> <span className="font-medium">{cont.tracking.tracking_data.pod_eta?.slice(0, 10)}</span></span>}
                    {cont.tracking.tracking_data.status && <Badge variant="secondary" className="text-[10px]">{cont.tracking.tracking_data.status?.replace(/_/g, ' ')}</Badge>}
                  </div>
                )}
              </div>

              {/* Expanded cars list with remove-car action */}
              {expandedId === cont.container_number && (
                <div className="border-t bg-slate-50/50">
                  <table className="w-full text-sm" data-testid={`container-cars-${cont.container_number}`}>
                    <thead>
                      <tr className="bg-slate-100 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        <th className="px-4 py-2 text-left">Order</th>
                        <th className="px-4 py-2 text-left">Car</th>
                        <th className="px-4 py-2 text-left">End Client</th>
                        <th className="px-4 py-2 text-left hidden sm:table-cell">VIN</th>
                        <th className="px-4 py-2 text-right w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {cont.cars.map(car => (
                        <tr key={car.id} className="hover:bg-white">
                          <td className="px-4 py-2">
                            <span className="font-mono text-xs font-medium text-primary">{car.order_number}</span>
                            {car.order_status && <Badge className={`ml-1 text-[9px] ${STATUS_COLORS[car.order_status] || 'bg-gray-100'}`}>{STATUS_LABELS[car.order_status] || car.order_status}</Badge>}
                          </td>
                          <td className="px-4 py-2 font-medium">{car.brand} {car.model} {car.color ? `(${car.color})` : ''}</td>
                          <td className="px-4 py-2 text-muted-foreground">{car.end_client_name || '-'}</td>
                          <td className="px-4 py-2 font-mono text-xs hidden sm:table-cell">{car.vin || '-'}</td>
                          <td className="px-4 py-2 text-right">
                            <button
                              type="button"
                              title="Remove from container"
                              className="text-destructive hover:bg-destructive/10 rounded p-1"
                              onClick={() => handleRemoveCar(cont.container_number, car.id, `${car.brand} ${car.model}`)}
                              data-testid={`remove-car-${car.id}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ===== Create / Assign Dialog ===== */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Container & Assign Cars</DialogTitle>
            <DialogDescription>Search cars by client name, order # or VIN. Each car can only belong to one container.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="create-container-form">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Container Number *</Label>
                <Input
                  value={form.container_number}
                  onChange={(e) => setForm({ ...form, container_number: e.target.value.toUpperCase() })}
                  placeholder="e.g. MSCU1234567"
                  required
                  className="h-9 font-mono uppercase"
                  data-testid="container-number-input"
                />
              </div>
              <div>
                <Label className="text-xs">Shipping Company</Label>
                <Input value={form.shipping_company} onChange={(e) => setForm({ ...form, shipping_company: e.target.value })} className="h-9" data-testid="container-company-input" />
              </div>
              <div>
                <Label className="text-xs">Vessel #</Label>
                <Input value={form.vessel_number} onChange={(e) => setForm({ ...form, vessel_number: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">BL #</Label>
                <Input value={form.bl_number} onChange={(e) => setForm({ ...form, bl_number: e.target.value })} className="h-9" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">ETA</Label>
                <Input type="date" value={form.eta?.slice(0, 10) || ''} onChange={(e) => setForm({ ...form, eta: e.target.value })} className="h-9" />
              </div>
            </div>

            {/* Car picker */}
            <div className="border rounded p-3 bg-slate-50 space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <Label className="text-xs font-bold flex-1">Pick cars to assign</Label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Switch checked={unassignedOnly} onCheckedChange={setUnassignedOnly} data-testid="unassigned-toggle" />
                  Show unassigned only
                </label>
              </div>
              <div className="flex gap-2">
                <Input
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                  placeholder="Search client name · order # · VIN…"
                  className="h-8 text-sm"
                  data-testid="container-car-search"
                />
                <Button type="button" size="sm" onClick={runSearch} disabled={pickerLoading} data-testid="container-car-search-btn">
                  <MagnifyingGlass className="w-4 h-4 mr-1" /> {pickerLoading ? '…' : 'Search'}
                </Button>
              </div>
              <div className="bg-white border rounded max-h-72 overflow-y-auto divide-y" data-testid="car-picker-list">
                {pickerResults.length === 0 ? (
                  <p className="text-xs text-muted-foreground p-3 italic">
                    {pickerLoading ? 'Searching…' : 'No results. Press Search or change the toggle.'}
                  </p>
                ) : (
                  pickerResults.map(c => {
                    const checked = form.car_ids.includes(c.id);
                    const assigned = !!c.container_number;
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-3 px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 ${checked ? 'bg-primary/5' : ''}`}
                        data-testid={`pick-car-${c.id}`}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleCar(c.id)} />
                        <span className="font-medium">{c.brand} {c.model}</span>
                        {c.color && <span className="text-muted-foreground">· {c.color}</span>}
                        {c.vin && <span className="font-mono text-[10px] text-muted-foreground">({c.vin})</span>}
                        <span className="text-muted-foreground flex-1 truncate">
                          {c.order_number} · {c.client_name || 'Client?'}
                        </span>
                        {assigned && (
                          <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-700">in {c.container_number}</Badge>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">{form.car_ids.length} car(s) selected</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="container-submit-btn">{saving ? 'Saving…' : 'Create & Assign'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== Conflict Dialog (cars already in another container) ===== */}
      <Dialog open={!!conflictDlg} onOpenChange={(open) => !open && setConflictDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warning weight="duotone" className="w-5 h-5 text-amber-600" /> Cars already in another container
            </DialogTitle>
            <DialogDescription>
              These cars are currently assigned to a different container. Move them to <span className="font-mono font-bold">{form.container_number}</span> anyway?
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-60 overflow-y-auto border rounded p-2 space-y-1" data-testid="conflict-list">
            {conflictDlg?.conflicts?.map(c => (
              <div key={c.car_id} className="flex items-center justify-between text-xs py-1">
                <span className="font-medium">{c.label}</span>
                <span className="text-muted-foreground">currently in <span className="font-mono font-semibold">{c.current_container}</span></span>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setConflictDlg(null)}>Cancel</Button>
            <Button onClick={confirmForceMove} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="conflict-force-btn">
              <ArrowsClockwise className="w-4 h-4 mr-1" /> Move cars
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Edit Container Dialog ===== */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Container {editTarget?.container_number}</DialogTitle>
            <DialogDescription>Rename, update shipping metadata, or add more cars. Changes apply to all {editTarget?.car_count} car(s) currently in this container.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4" data-testid="edit-container-form">
            <div>
              <Label className="text-xs">Container Number</Label>
              <Input
                value={editForm.new_container_number}
                onChange={(e) => setEditForm({ ...editForm, new_container_number: e.target.value.toUpperCase() })}
                className="h-9 font-mono uppercase"
                data-testid="edit-container-number"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Shipping Company</Label>
                <Input value={editForm.shipping_company} onChange={(e) => setEditForm({ ...editForm, shipping_company: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">Vessel #</Label>
                <Input value={editForm.vessel_number} onChange={(e) => setEditForm({ ...editForm, vessel_number: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">BL #</Label>
                <Input value={editForm.bl_number} onChange={(e) => setEditForm({ ...editForm, bl_number: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">ETA</Label>
                <Input type="date" value={editForm.eta?.slice(0, 10) || ''} onChange={(e) => setEditForm({ ...editForm, eta: e.target.value })} className="h-9" />
              </div>
            </div>

            {/* Add more cars */}
            <div className="border rounded p-3 bg-slate-50 space-y-2">
              <div className="flex items-center gap-3 flex-wrap">
                <Label className="text-xs font-bold flex-1">Add more cars to this container</Label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Switch checked={editUnassignedOnly} onCheckedChange={setEditUnassignedOnly} data-testid="edit-unassigned-toggle" />
                  Show unassigned only
                </label>
              </div>
              <div className="flex gap-2">
                <Input
                  value={editPickerQuery}
                  onChange={(e) => setEditPickerQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runEditSearch(); } }}
                  placeholder="Search client name · order # · VIN…"
                  className="h-8 text-sm"
                  data-testid="edit-car-search"
                />
                <Button type="button" size="sm" onClick={runEditSearch} disabled={editPickerLoading} data-testid="edit-car-search-btn">
                  <MagnifyingGlass className="w-4 h-4 mr-1" /> {editPickerLoading ? '…' : 'Search'}
                </Button>
              </div>
              <div className="bg-white border rounded max-h-56 overflow-y-auto divide-y" data-testid="edit-car-picker-list">
                {editPickerResults.length === 0 ? (
                  <p className="text-xs text-muted-foreground p-3 italic">
                    {editPickerLoading ? 'Searching…' : 'No results. Try toggling "Show unassigned only" off to find cars in other containers.'}
                  </p>
                ) : (
                  editPickerResults.map(c => {
                    const checked = editCarIdsToAdd.includes(c.id);
                    const assigned = !!c.container_number;
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-3 px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 ${checked ? 'bg-primary/5' : ''}`}
                        data-testid={`edit-pick-car-${c.id}`}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleEditCar(c.id)} />
                        <span className="font-medium">{c.brand} {c.model}</span>
                        {c.color && <span className="text-muted-foreground">· {c.color}</span>}
                        {c.vin && <span className="font-mono text-[10px] text-muted-foreground">({c.vin})</span>}
                        <span className="text-muted-foreground flex-1 truncate">
                          {c.order_number} · {c.client_name || 'Client?'}
                        </span>
                        {assigned && (
                          <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-700">in {c.container_number}</Badge>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">{editCarIdsToAdd.length} car(s) selected to add</p>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="edit-container-save">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContainersPage;
